import { useEffect, useRef } from 'react'
import { AnyAction } from 'redux'
import { actionObserverRegistry } from '../redux/middleware/observerMiddleware'

type ActionPredicate = (action: AnyAction) => boolean
type ActionHandler = (action: AnyAction) => void

interface UseActionObserverOptions {
  predicate?: ActionPredicate
  handler: ActionHandler
  enabled?: boolean
}

/**
 * Hook to observe Redux actions that pass through the store
 * @param options.predicate - Optional function to filter which actions to observe
 * @param options.handler - Function called when a matching action is observed
 * @param options.enabled - Whether the observer is active (default: true)
 */
export function useActionObserver({
  predicate = () => true,
  handler,
  enabled = true,
}: UseActionObserverOptions): void {
  const handlerRef = useRef(handler)
  const predicateRef = useRef(predicate)

  // Keep refs updated
  useEffect(() => {
    handlerRef.current = handler
    predicateRef.current = predicate
  })

  useEffect(() => {
    if (!enabled) return

    // Create observer function that checks predicate
    const observer = (action: AnyAction) => {
      if (predicateRef.current(action)) {
        handlerRef.current(action)
      }
    }

    // Subscribe to the registry
    const unsubscribe = actionObserverRegistry.subscribe(observer)

    // Cleanup on unmount or when enabled changes
    return unsubscribe
  }, [enabled])
}

interface UseActionObserverByTypeOptions<T extends AnyAction = AnyAction> {
  actionType: string | string[]
  handler: (action: T) => void
  enabled?: boolean
}

/**
 * Hook to observe Redux actions of specific types
 * @param options.actionType - Single action type or array of action types to observe
 * @param options.handler - Function called when a matching action is observed
 * @param options.enabled - Whether the observer is active (default: true)
 */
export function useActionObserverByType<T extends AnyAction = AnyAction>({
  actionType,
  handler,
  enabled = true,
}: UseActionObserverByTypeOptions<T>): void {
  const actionTypes = Array.isArray(actionType) ? actionType : [actionType]

  useActionObserver({
    predicate: action => actionTypes.includes(action.type),
    handler: handler as ActionHandler,
    enabled,
  })
}

interface UseActionObserverByModuleOptions<T extends AnyAction = AnyAction> {
  module: string
  handler: (action: T) => void
  enabled?: boolean
}

/**
 * Hook to observe Redux actions from a specific module
 * @param options.module - Module name to observe (e.g., 'Decision', 'Risk', 'System')
 * @param options.handler - Function called when a matching action is observed
 * @param options.enabled - Whether the observer is active (default: true)
 */
export function useActionObserverByModule<T extends AnyAction = AnyAction>({
  module,
  handler,
  enabled = true,
}: UseActionObserverByModuleOptions<T>): void {
  useActionObserver({
    predicate: action => action.module === module,
    handler: handler as ActionHandler,
    enabled,
  })
}
